import React, { useEffect, useMemo, useState } from "react";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import localeText from "../../../../../../locale";
import { useToastContext } from "../../../../../../contexts/ToastContext/ToastContext";
import useProfileAnalysisForm from "./hooks/useProfileAnalysisForm";
import { InputMask } from "primereact/inputmask";

const MAX_TEXT = 200;

const MARITAL_STATUS_OPTIONS = [
  { value: "single", labelKey: "profileAnalysisMaritalSingle" },
  { value: "married", labelKey: "profileAnalysisMaritalMarried" },
  { value: "divorced", labelKey: "profileAnalysisMaritalDivorced" },
  { value: "widowed", labelKey: "profileAnalysisMaritalWidowed" },
  { value: "civil_union", labelKey: "profileAnalysisMaritalCivilUnion" },
  { value: "separated", labelKey: "profileAnalysisMaritalSeparated" },
];

const COUNTRY_CODE_OPTIONS = [
  { value: "+39", label: "+39" },
  { value: "+34", label: "+34" },
  { value: "+351", label: "+351" },
  { value: "+1", label: "+1" },
];

const ProfileAnalysisForm = () => {
  const { showSuccessToast, showErrorToast } = useToastContext();
  const { formData, loading, saving, submit } = useProfileAnalysisForm();
  const [localData, setLocalData] = useState(formData);

  useEffect(() => {
    setLocalData(formData);
  }, [formData]);

  const maritalStatusOptions = useMemo(
    () =>
      MARITAL_STATUS_OPTIONS.map((item) => ({
        value: item.value,
        label: localeText(item.labelKey) || item.value,
      })),
    [],
  );

  const objectivesLength = (localData?.goals_and_needs?.objectives || "").length;
  const needsLength = (localData?.goals_and_needs?.needs || "").length;

  const updatePersonalInfo = (key, value) => {
    setLocalData((prev) => ({
      ...prev,
      personal_info: {
        ...prev.personal_info,
        [key]: value,
      },
    }));
  };

  const updatePhone = (key, value) => {
    setLocalData((prev) => ({
      ...prev,
      personal_info: {
        ...prev.personal_info,
        phone: {
          ...prev.personal_info.phone,
          [key]: value,
        },
      },
    }));
  };

  const updateGoals = (key, value) => {
    setLocalData((prev) => ({
      ...prev,
      goals_and_needs: {
        ...prev.goals_and_needs,
        [key]: (value || "").slice(0, MAX_TEXT),
      },
    }));
  };

  const updateConsent = (value) => {
    setLocalData((prev) => ({
      ...prev,
      meta: {
        ...prev.meta,
        consent: !!value,
      },
    }));
  };

  const onSubmit = async () => {
    try {
      await submit(localData, { finalize: true });
      showSuccessToast({
        detail: "profileAnalysisSubmitSuccess" || "Diagnosi inviata con successo.",
      });
    } catch (error) {
      showErrorToast({ code: error?.backendCode || 500000, detail: "profileAnalysisSubmitError" });
    }
  };

  if (loading) {
    return (
      <div
        className="flex align-items-center justify-content-center gap-2"
        style={{ color: "var(--theme-text-muted)" }}
      >
        <i className="pi pi-spin pi-spinner" />
        <span>{localeText("loading") || "Carregando..."}</span>
      </div>
    );
  }

  return (
    <div
      className="w-full mx-auto"
      style={{ maxWidth: "700px", marginLeft: "0", minWidth: "300px" }}
    >
      <div className="p-fluid grid mt-2">
        <div className="col-12 md:col-6">
          <label className="block text-sm mb-1">{localeText("firstName") || "Nome"} *</label>
          <InputText
            value={localData.personal_info.first_name}
            onChange={(e) => updatePersonalInfo("first_name", e.target.value)}
            className="w-full border-round-md p-no-outline"
          />
        </div>
        <div className="col-12 md:col-6">
          <label className="block text-sm mb-1">{localeText("surname") || "Cognome"} *</label>
          <InputText
            value={localData.personal_info.last_name}
            onChange={(e) => updatePersonalInfo("last_name", e.target.value)}
            className="w-full border-round-md p-no-outline"
          />
        </div>
        <div className="col-12">
          <label className="block text-sm mb-1">
            {localeText("profileAnalysisAddress") || "Indirizzo"} *
          </label>
          <InputText
            value={localData.personal_info.address}
            onChange={(e) => updatePersonalInfo("address", e.target.value)}
            className="w-full border-round-md p-no-outline"
          />
          <small className="text-color-secondary">
            {localeText("profileAnalysisAddressHelp") ||
              "Inserisci il tuo indirizzo di residenza attuale."}
          </small>
        </div>
        <div className="col-12 md:col-4">
          <label className="block text-sm mb-1">
            {localeText("profileAnalysisNationality") || "Nazionalità"} *
          </label>
          <InputText
            value={localData.personal_info.nationality}
            onChange={(e) => updatePersonalInfo("nationality", e.target.value)}
            className="w-full border-round-md p-no-outline"
          />
        </div>
        <div className="col-12 md:col-4">
          <label className="block text-sm mb-1">
            {localeText("profileAnalysisBirthPlace") || "Luogo di Nascita"} *
          </label>
          <InputText
            value={localData.personal_info.birth_place}
            onChange={(e) => updatePersonalInfo("birth_place", e.target.value)}
            className="w-full border-round-md p-no-outline"
          />
        </div>
        <div className="col-12 md:col-4">
          <label className="block text-sm mb-1">
            {localeText("profileAnalysisBirthDate") || "Data di Nascita"} *
          </label>
          <InputMask
            mask="99/99/9999"
            unmask={false}
            value={localData.personal_info.birth_date}
            onChange={(e) => updatePersonalInfo("birth_date", e.target.value)}
            placeholder={localeText("profileAnalysisBirthDatePlaceholder") || "GG/MM/AAAA"}
            className="w-full border-round-md p-no-outline"
          />
        </div>
        <div className="col-12 md:col-6">
          <label className="block text-sm mb-1">
            {localeText("profileAnalysisPhone") || "Telefono"} *
          </label>
          <div className="flex gap-2">
            {/* <Dropdown
              value={localData.personal_info.phone.country_code}
              options={COUNTRY_CODE_OPTIONS}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => updatePhone("country_code", e.value)}
              className="w-5 border-round-md p-no-outline"
            /> */}
            <InputText
              value={localData.personal_info.phone.country_code}
              onChange={(e) => updatePhone("country_code", e.target.value)}
              placeholder="+39"
              className="w-3 border-round-md p-no-outline"
            />
            <InputText
              value={localData.personal_info.phone.number}
              onChange={(e) => updatePhone("number", e.target.value)}
              placeholder="06 1234567"
              className="w-full border-round-md p-no-outline"
            />
          </div>
        </div>
        <div className="col-12 md:col-6">
          <label className="block text-sm mb-1">
            {localeText("profileAnalysisEmail") || "E-mail"} *
          </label>
          <InputText
            value={localData.personal_info.email}
            onChange={(e) => updatePersonalInfo("email", e.target.value)}
            className="w-full border-round-md p-no-outline"
          />
        </div>
        <div className="col-12 md:col-6">
          <label className="block text-sm mb-1">
            {localeText("profileAnalysisEmployment") || "Attività Lavorativa"} *
          </label>
          <InputText
            value={localData.personal_info.employment}
            onChange={(e) => updatePersonalInfo("employment", e.target.value)}
            className="w-full border-round-md p-no-outline"
          />
        </div>
        <div className="col-12 md:col-6">
          <label className="block text-sm mb-1">
            {localeText("profileAnalysisMaritalStatus") || "Stato Civile"} *
          </label>
          <Dropdown
            value={localData.personal_info.marital_status}
            options={maritalStatusOptions}
            onChange={(e) => updatePersonalInfo("marital_status", e.value)}
            placeholder={localeText("select") || "Seleziona"}
            className="w-full border-round-md p-no-outline"
          />
        </div>
        <div className="col-12 md:col-6">
          <label className="block text-sm mb-1">
            {localeText("profileAnalysisEducationLevel") || "Titolo di Studio"} *
          </label>
          <InputText
            value={localData.personal_info.education_level}
            onChange={(e) => updatePersonalInfo("education_level", e.target.value)}
            className="w-full border-round-md p-no-outline"
          />
        </div>
        <div className="col-12 md:col-6">
          <label className="block text-sm mb-1">
            {localeText("profileAnalysisHouseholdMembers") || "Componenti Nucleo Familiare"} *
          </label>
          <InputNumber
            value={localData.personal_info.household_members}
            onValueChange={(e) => updatePersonalInfo("household_members", e.value)}
            className="w-full border-round-md p-no-outline"
            min={0}
            useGrouping={false}
          />
        </div>
        <div className="col-12">
          <label className="block text-sm mb-1">
            {localeText("profileAnalysisObjectives") || "Obiettivi"}
          </label>
          <InputTextarea
            value={localData.goals_and_needs.objectives}
            onChange={(e) => updateGoals("objectives", e.target.value)}
            rows={4}
            className="w-full border-round-md p-no-outline"
          />
          <div className="text-right text-xs">
            {objectivesLength}/{MAX_TEXT}
          </div>
        </div>
        <div className="col-12">
          <label className="block text-sm mb-1">
            {localeText("profileAnalysisNeeds") || "Esigenze"}
          </label>
          <InputTextarea
            value={localData.goals_and_needs.needs}
            onChange={(e) => updateGoals("needs", e.target.value)}
            rows={4}
            className="w-full border-round-md p-no-outline"
          />
          <div className="text-right text-xs">
            {needsLength}/{MAX_TEXT}
          </div>
        </div>
      </div>
      <hr className="my-4" />
      <div className="surface-border">
        <div className="grid">
          <div className="col-12 md:col-6">
            <span className="text-sm font-medium">
              {localeText("profileAnalysisDateLabel") || "Data: "}
            </span>
            <span className="text-sm">
              {localData.meta.date ||
                localData.meta.created_at ||
                localeText("profileAnalysisNoData") ||
                "—"}
            </span>
          </div>
          <div className="col-12 md:col-6">
            <span className="text-sm font-medium">
              {localeText("profileAnalysisConsultantLabel") || "Consulente: "}
            </span>
            <span className="text-sm">
              {localData.meta.consultant || localeText("profileAnalysisNoData") || "—"}
            </span>
          </div>
        </div>
      </div>

      <div className="flex align-items-start gap-2 mt-3">
        <Checkbox
          inputId="profile-analysis-consent"
          checked={!!localData.meta.consent}
          onChange={(e) => updateConsent(e.checked)}
        />
        <label htmlFor="profile-analysis-consent" className="text-sm">
          <strong>{localeText("profileAnalysisConsentLabel") || "Acconsento"}</strong>
          <div className="text-color-secondary mt-1">
            {localeText("profileAnalysisConsentText") ||
              "Le informazioni vengono utilizzate per elaborare una valutazione gratuita ai fini della crescita della piattaforma EXTRACONOMY."}
          </div>
          <div className="text-color-secondary mt-2">
            {localeText("profileAnalysisAuthorizeText") ||
              "Autorizzo il trattamento dei miei dati personali ai sensi del Decreto Legislativo 101/2018 e dell’art. 13 del GDPR (Regolamento UE 2016/679) ai fini della valutazione."}
          </div>
        </label>
      </div>

      <hr className="my-4" />

      <div className="flex justify-content-end">
        <Button
          type="button"
          label={
            localData.meta.id
              ? localeText("profileAnalysisUpdateDiagnosis")
              : localeText("profileAnalysisSendDiagnosis") || "Invia Diagnosi"
          }
          loading={saving}
          disabled={saving || !localData.meta.consent}
          onClick={onSubmit}
          className="border-round-md p-no-outline text-sm font-medium"
          style={{ backgroundColor: "var(--theme-primary)", color: "#fff", border: "none", height: "40px" }}
        />
      </div>
    </div>
  );
};

export default ProfileAnalysisForm;
